import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Building2,
  HardHat,
  Inbox,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareText,
  Package,
  Receipt,
  Server,
  Briefcase,
  Factory,
  UserCircle,
  Settings,
  Users,
  X,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { api, unwrap } from "@/lib/api";
import { ADMIN_ROLES, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { StorageBanner } from "@/components/StorageBanner";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", key: "nav.dashboard", icon: LayoutDashboard, end: true, adminOnly: false },
  { to: "/analytics", key: "nav.analytics", icon: BarChart3, end: false, adminOnly: false },
  { to: "/properties", key: "nav.properties", icon: Building2, end: false, adminOnly: false },
  { to: "/catalog", key: "nav.catalog", icon: Package, end: false, adminOnly: false },
  { to: "/leads", key: "nav.leads", icon: MessageSquareText, end: false, adminOnly: false },
  { to: "/sales", key: "nav.sales", icon: Receipt, end: false, adminOnly: false },
  { to: "/customers", key: "nav.customers", icon: Users, end: false, adminOnly: true },
  { to: "/electricians", key: "nav.electricians", icon: HardHat, end: false, adminOnly: true },
  { to: "/claims", key: "nav.claims", icon: Inbox, end: false, adminOnly: true },
  { to: "/server", key: "nav.server", icon: Server, end: false, adminOnly: true, superOnly: true },
  {
    to: "/tenants",
    key: "nav.tenants",
    icon: Briefcase,
    end: false,
    adminOnly: true,
    superOnly: true,
  },
  { to: "/company", key: "nav.company", icon: Factory, end: false, adminOnly: true },
  {
    to: "/settings",
    key: "nav.settings",
    icon: Settings,
    end: false,
    adminOnly: true,
    superOnly: true,
  },
] as const;

function Brand() {
  return (
    <span className="flex items-center gap-2.5 text-lg font-extrabold tracking-tight">
      <span className="hero-gradient shadow-pop flex size-9 items-center justify-center rounded-xl text-white">
        <Zap className="size-5 fill-current" />
      </span>
      Elektr Pasport
    </span>
  );
}

const ROLE_KEYS = {
  super_admin: "role.super_admin",
  admin: "role.admin",
  electrician: "role.electrician",
  customer: "role.customer",
} as const;

export function AppLayout() {
  const { t, lang, setLang } = useT();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const admin = !!user && ADMIN_ROLES.includes(user.role);
  const pendingClaims = useQuery({
    queryKey: ["claims-count"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/claims/", { params: { query: { status: "pending", page_size: 1 } } }),
      ).count,
    enabled: admin,
    refetchInterval: 60_000,
  });
  const items = NAV.filter(
    (n) =>
      (!n.adminOnly || (user && ADMIN_ROLES.includes(user.role))) &&
      (!("superOnly" in n) || user?.role === "super_admin"),
  );

  const nav = (
    <nav aria-label="main" className="flex flex-col gap-1 p-3">
      {items.map(({ to, key, icon: Icon, end }, i) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={() => setOpen(false)}
          style={{ ["--i" as string]: i }}
          className={({ isActive }) =>
            cn(
              "anim-fade-up group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition-all duration-150",
              isActive
                ? "hero-gradient shadow-pop text-white"
                : "text-muted-foreground hover:translate-x-0.5 hover:bg-muted hover:text-foreground",
            )
          }
        >
          <Icon className="size-5 transition-transform duration-150 group-hover:scale-110" />
          {t(key)}
          {to === "/claims" && !!pendingClaims.data && (
            <span
              className="ml-auto rounded-full bg-danger px-2 text-xs font-bold text-white"
              aria-label={String(pendingClaims.data)}
            >
              {pendingClaims.data}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen md:grid md:grid-cols-[15rem_1fr]">
      {/* planshet/kompyuter: doimiy sidebar */}
      <aside className="glass hidden border-r border-border md:block">
        <div className="sticky top-0">
          <div className="flex h-16 items-center px-5">
            <Brand />
          </div>
          {nav}
        </div>
      </aside>

      {/* telefon: ochiladigan menyu */}
      {open && (
        <div className="fixed inset-0 z-30 md:hidden">
          <button
            aria-label={t("common.close")}
            className="absolute inset-0 bg-black/50"
            onClick={() => setOpen(false)}
          />
          <aside className="anim-fade-up absolute inset-y-0 left-0 w-64 overflow-y-auto bg-card shadow-xl">
            <div className="flex h-16 items-center justify-between px-5">
              <Brand />
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("common.close")}
                onClick={() => setOpen(false)}
              >
                <X className="size-5" />
              </Button>
            </div>
            {nav}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-col">
        <header className="glass sticky top-0 z-20 flex min-h-14 items-center gap-3 border-b border-border px-3 pt-[env(safe-area-inset-top)] md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={t("nav.menu")}
            onClick={() => setOpen(true)}
          >
            <Menu className="size-5" />
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <div
              className="flex overflow-hidden rounded-lg border border-border"
              role="group"
              aria-label="language"
            >
              {(["uz", "ru"] as const).map((l) => (
                <button
                  key={l}
                  aria-pressed={lang === l}
                  onClick={() => setLang(l)}
                  className={cn(
                    "min-h-9 px-3 text-xs font-bold uppercase",
                    lang === l ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                  )}
                >
                  {l}
                </button>
              ))}
            </div>
            <ThemeToggle />
            <NavLink
              to="/profile"
              aria-label={t("profile.title")}
              className="flex min-h-9 items-center gap-2 rounded-lg px-2 hover:bg-muted"
            >
              <UserCircle className="size-5 shrink-0" />
              <span className="hidden text-right text-xs leading-tight sm:block">
                <span className="block font-semibold">{user?.full_name || user?.phone}</span>
                <span className="block text-muted-foreground">
                  {user ? t(ROLE_KEYS[user.role]) : ""}
                </span>
              </span>
            </NavLink>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("auth.logout")}
              onClick={() => void logout()}
            >
              <LogOut className="size-5" />
            </Button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-6xl flex-1 p-3 pb-[calc(var(--badge-safe)+env(safe-area-inset-bottom))] md:p-6">
          {user?.role === "super_admin" && <StorageBanner />}
          <div key={loc.pathname} className="anim-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
